/**
 * Departure time control — spec v0.2 §2.1.
 *
 * "Route me at 15:00" is a product feature, not a debugging aid: shade depends
 * entirely on when you walk, and the most useful question this app answers is
 * often "what if I leave later?"
 *
 * The date matters too, because it selects the seasonal shadow set (§6.5) and
 * whether the app is in season at all.
 */

export interface WhenControl {
  /** ISO timestamp to send to the API. */
  value(): string
  /** True when the user has not moved anything off "now". */
  isNow(): boolean
  onChange(fn: () => void): void
  /** Snap back to the current time. */
  reset(): void
}

function pad(n: number): string {
  return String(n).padStart(2, '0')
}

function toDateInput(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

export function createWhenControl(
  hourEl: HTMLInputElement,
  dateEl: HTMLInputElement,
  textEl: HTMLOutputElement,
  nowBtn: HTMLButtonElement,
): WhenControl {
  let touched = false
  const listeners: Array<() => void> = []

  function render() {
    const hour = Number(hourEl.value)
    textEl.textContent = touched ? `${pad(hour)}:00` : 'now'
    textEl.classList.toggle('is-now', !touched)
  }

  function syncToNow() {
    const now = new Date()
    hourEl.value = String(now.getHours())
    dateEl.value = toDateInput(now)
    touched = false
    render()
  }

  function changed() {
    touched = true
    render()
    for (const fn of listeners) fn()
  }

  hourEl.addEventListener('input', () => {
    touched = true
    render()
  })
  // Re-route on release, not on every pixel of the drag.
  hourEl.addEventListener('change', changed)
  dateEl.addEventListener('change', changed)

  nowBtn.addEventListener('click', () => {
    syncToNow()
    for (const fn of listeners) fn()
  })

  syncToNow()

  return {
    value() {
      if (!touched) return new Date().toISOString()
      // Build a local-time date so the hour the user picked is the hour they
      // meant; the API converts to the city's buckets.
      const [y, m, d] = dateEl.value.split('-').map(Number)
      const when = new Date(y, m - 1, d, Number(hourEl.value), 0, 0, 0)
      return when.toISOString()
    },
    isNow: () => !touched,
    onChange(fn) {
      listeners.push(fn)
    },
    reset: syncToNow,
  }
}
